import React, { useMemo } from "react";
import { Chart, registerables } from "chart.js";
import AreaChart from "./AreaChart";
import Text from "@/components/ui/text";
import { StageData } from "@/entities/reporting/types";
import MRRTooltip from "@/components/controls/MRRTooltip";
import { displayTotalCurrency, reducePrecision } from "@/utils/numbers";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { maxBy } from "lodash";

const CHART_HEIGHT = 330;

export type pipelineFunnelChartPropsType = {
  stagesData: StageData[];
  isArr: boolean;
  isProbabilityMode: boolean;
  isAE: boolean;
};

Chart.register(...registerables);

const PipelineFunnelChart: React.FC<pipelineFunnelChartPropsType> = ({
  stagesData,
  isArr,
  isProbabilityMode,
  isAE
}) => {
  const maxYValue = useMemo(() => {
    if (isProbabilityMode)
      return (
        (maxBy(stagesData, "totalWeightedValue")?.totalWeightedValue ?? 0) *
        1.75
      );
    return (maxBy(stagesData, "totalValue")?.totalValue ?? 0) * 1.05;
  }, [stagesData, isProbabilityMode]);

  return (
    <div className="max-h-full w-full grid grid-flow-col auto-cols-[minmax(0,_1fr)] text-center">
      {stagesData.map((stageDataSlice, index) => (
        <div
          key={stageDataSlice.stageId}
          className="relative flex flex-col max-h-full pl-[2px]"
        >
          {index > 0 && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="100%"
                stroke="var(--widget)"
                strokeWidth="1"
                strokeDasharray="5,5"
              />
            </svg>
          )}
          <div className="flex flex-col gap-5 mx-1.5">
            <Tooltip data-test="pipeline-funnel-stagename-tooltip">
              <TooltipTrigger>
                <Text
                  as="div"
                  variant="bodySecondary"
                  className="py-1 text-nowrap truncate"
                >
                  {stageDataSlice.stageName}
                </Text>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{stageDataSlice.stageName}</p>
              </TooltipContent>
            </Tooltip>
            <Text
              data-test="pipeline-funnel-total-display"
              variant="lgSubtitleH2"
            >
              <MRRTooltip
                trigger={displayTotalCurrency(
                  reducePrecision(
                    (isProbabilityMode
                      ? stageDataSlice.totalWeightedValue
                      : stageDataSlice.totalValue) ?? 0
                  )
                )}
                index={index}
                stagesData={stagesData}
                isArr={isArr}
                isProbabilityMode={isProbabilityMode}
              />
            </Text>
          </div>
          <AreaChart
            datasets={stagesData}
            index={index}
            maxYValue={maxYValue}
            height={CHART_HEIGHT}
            isArr={isArr}
            isProbabilityMode={isProbabilityMode}
            isAE={isAE}
          />
        </div>
      ))}
    </div>
  );
};

export default PipelineFunnelChart;
